import { DeliveryDatesFetchState } from '@app/pages/api/delivery-dates/types';
import logger from '@redbubble/next/logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CartCheckoutClient } from '../../cartCheckoutClient';
import {
  DispatchActionsType,
  ErrorCode,
  ErrorCodeType,
  StateDispatch,
} from '../types';
import { getUniqueProductTypeIds } from '../utils';
import {
  AsyncHookState,
  AsyncRequestResult,
  StateCode,
} from './AsyncHookState';

type HookState = AsyncHookState<ErrorCodeType>;
type AsyncResult = AsyncRequestResult<ErrorCodeType>;

export const useGetCartAndDeliveryDates = (
  client: CartCheckoutClient,
  dispatch: StateDispatch
) => {
  const abortController = useRef<AbortController>();
  const [result, setResult] = useState<HookState>({});

  const handler = useCallback(
    async ({
      deliveryDatesFetchState,
    }: {
      deliveryDatesFetchState: DeliveryDatesFetchState;
    }): Promise<AsyncResult> => {
      // Abort any pending requests
      if (abortController.current) {
        abortController.current.abort();
      }
      const newAbortController = new AbortController();
      abortController.current = newAbortController;

      setResult((prev) => ({ ...prev, state: StateCode.LOADING }));
      dispatch({ type: DispatchActionsType.SET_CART_LOADING, payload: true });

      return client
        .fetchCartAndUpdateShippingCountry()
        .then(async (cart) => {
          dispatch({ type: DispatchActionsType.SET_CART, payload: cart });

          dispatch({
            type: DispatchActionsType.SET_DELIVERY_DATES_LOADING,
            payload: true,
          });

          const productTypeIds = getUniqueProductTypeIds(cart.items);

          // we have successfully loaded the cart, we can now fetch delivery dates
          return client
            .fetchDeliveryDates({
              countryCode: cart.summary.estimated_shipping.country_code,
              productTypeIds,
              fetchState: deliveryDatesFetchState,
            })
            .then((deliveryDatesData) => {
              dispatch({
                type: DispatchActionsType.SET_DELIVERY_DATES,
                payload: deliveryDatesData,
              });

              return {
                state: StateCode.SUCCESS,
              };
            });
        })
        .catch((error) => {
          const errMessage =
            error instanceof Error ? error.message : String(error);

          const message = `Failed to refresh cart: ${errMessage}`;
          logger.error(
            {
              err: error,
              context: client.logging.context,
              message,
            },
            message
          );

          dispatch({
            type: DispatchActionsType.SET_ALL_LOADING,
            payload: false,
          });
          dispatch({
            type: DispatchActionsType.SET_ERRORS,
            payload: [
              {
                code: ErrorCode.CART_FETCH_ERROR,
                severity: 'critical',
              },
            ],
          });

          return {
            state: StateCode.FAILED,
            errors: [
              {
                code: ErrorCode.CART_FETCH_ERROR,
                exception: error,
              },
            ],
          };
        })
        .then((r) => {
          setResult(r);
          return r;
        });
    },
    [client, dispatch]
  );

  return { ...result, abortController, handler };
};

export const useFetchCartAndDeliveryDates = (
  client: CartCheckoutClient,
  dispatch: StateDispatch
) => {
  const result = useGetCartAndDeliveryDates(client, dispatch);

  useEffect(() => {
    result.handler({
      deliveryDatesFetchState: 'delivery-dates-fetch',
    });
  }, [result.handler]);

  return result;
};
